import 'remixicon/fonts/remixicon.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import App from './App.vue'
import router from './routes'
import vueDompurifyHTMLPlugin from 'vue-dompurify-html'
import { useLoadingStore } from '@/store/loading'
import { showError } from '@/utils/alert/alertBridge'
import { toUserFriendlyMessage } from '@cnd_fe/ky-client'

// 전역 CSS - 중요한 순서대로 import (URL 방식으로 명시적 로드)
import contentCSS from './assets/css/content.css?url'
import content01CSS from './assets/css/content01.css?url'
import content02CSS from './assets/css/content02.css?url'
import mainCSS from './assets/css/main.css?url'

// CSS 파일들을 명시적으로 로드
const loadCSS = (href) => {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

// 순서대로 CSS 로드
loadCSS(contentCSS)
loadCSS(content01CSS)
loadCSS(content02CSS)
loadCSS(mainCSS)

// 나머지 CSS들도 일반 import로 추가
import './assets/css/coreui-modal.css'
import './assets/css/print.css'
import './assets/css/vote.css'

import { ContentBody, ContentHeader, ContentHeading } from './components/layout'
import LoadingOverlay from '@/components/feedback/LoadingOverlay.vue'

// Pinia 설정
const pinia = createPinia()
pinia.use(
  createPersistedState({
    auto: true
  })
)

const app = createApp(App)

// sessionStorage의 오래된 lv_reenter 플래그들 정리
try {
  const keys = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key && key.startsWith('lv_reenter:')) {
      keys.push(key)
    }
  }
  // 앱 시작 시 모든 lv_reenter 플래그 초기화 (새로 시작하므로)
  keys.forEach(key => sessionStorage.removeItem(key))
} catch (e) {
  // sessionStorage 접근 실패 시 무시
}

// 전역 프린트 함수 등록
app.config.globalProperties.$print = () => window.print()

app.use(pinia)
app.use(router)
app.use(vueDompurifyHTMLPlugin)

router.beforeEach((to, from, next) => {
  const loading = useLoadingStore()
  // 이전 페이지에 남아있는 로딩 상태를 초기화한 뒤 새 페이지 로딩 시작
  // (count 기반 카운터이므로 hide()를 한 번만 호출하면 누적 count를 소진 못함)
  loading.reset()
  loading.show('Loading...')
  next()
})

router.afterEach(() => {
  const loading = useLoadingStore()
  loading.hide()
})

router.onError(() => {
  const loading = useLoadingStore()
  loading.reset()
})

// 전역 컴포넌트 등록
app.component('ContentHeader', ContentHeader)
app.component('ContentBody', ContentBody)
app.component('ContentHeading', ContentHeading)
app.component('LoadingOverlay', LoadingOverlay)


app.config.errorHandler = (err) => {
  console.error('[globalErrorHandler]', err)
  // ky HTTPError: beforeError 훅에서 파싱한 serverMessage 사용
  const serverMsg = err?.serverMessage || err?.responseBody?.message || err?.responseBody?.data?.message
  if (serverMsg) {
    showError(toUserFriendlyMessage(serverMsg))
  } else if (err?.message) {
    // serverMessage가 없는 경우 err.message에서 기술 메시지 변환 시도
    const friendly = toUserFriendlyMessage(err.message)
    if (friendly !== err.message) {
      showError(friendly)
    }
  }
  // 변환 대상이 아닌 일반 에러는 console만 남기고 toast는 생략
  // (각 페이지에서 처리하도록)
}

window.addEventListener('unhandledrejection', (e) => {
  const err = e.reason
  console.error('[unhandledRejection]', err)
  // ky HTTPError: beforeError 훅에서 파싱한 serverMessage 사용
  const serverMsg = err?.serverMessage || err?.responseBody?.message || err?.responseBody?.data?.message
  if (serverMsg) {
    e.preventDefault()
    showError(toUserFriendlyMessage(serverMsg))
  } else if (err?.message) {
    const friendly = toUserFriendlyMessage(err.message)
    if (friendly !== err.message) {
      e.preventDefault()
      showError(friendly)
    }
  }
  // 변환 대상이 아닌 에러는 브라우저 기본 동작(콘솔 에러)으로 처리
})

app.mount('#app')


